import type { GeoPoint } from "./types";

/**
 * Plausible Eversend corridor weights.
 *
 * These are hand-tuned for demo mode only — production derives these from
 * a rolling BigQuery aggregate. Weights are relative; the sampler normalizes.
 */
export type CityId = string;

type CityDef = GeoPoint & { id: CityId; tier: 1 | 2 | 3 };

export const CITIES: CityDef[] = [
  // Africa — core Eversend corridors
  { id: "lagos", lat: 6.5244, lng: 3.3792, city: "Lagos", country: "NG", tier: 1 },
  { id: "abuja", lat: 9.0579, lng: 7.4951, city: "Abuja", country: "NG", tier: 2 },
  { id: "nairobi", lat: -1.2921, lng: 36.8219, city: "Nairobi", country: "KE", tier: 1 },
  { id: "mombasa", lat: -4.0435, lng: 39.6682, city: "Mombasa", country: "KE", tier: 3 },
  { id: "kampala", lat: 0.3476, lng: 32.5825, city: "Kampala", country: "UG", tier: 1 },
  { id: "kigali", lat: -1.9706, lng: 30.1044, city: "Kigali", country: "RW", tier: 2 },
  { id: "daressalaam", lat: -6.7924, lng: 39.2083, city: "Dar es Salaam", country: "TZ", tier: 2 },
  { id: "accra", lat: 5.6037, lng: -0.187, city: "Accra", country: "GH", tier: 1 },
  { id: "johannesburg", lat: -26.2041, lng: 28.0473, city: "Johannesburg", country: "ZA", tier: 1 },
  { id: "capetown", lat: -33.9249, lng: 18.4241, city: "Cape Town", country: "ZA", tier: 2 },
  { id: "cairo", lat: 30.0444, lng: 31.2357, city: "Cairo", country: "EG", tier: 2 },
  { id: "addis", lat: 9.03, lng: 38.74, city: "Addis Ababa", country: "ET", tier: 3 },
  { id: "casablanca", lat: 33.5731, lng: -7.5898, city: "Casablanca", country: "MA", tier: 3 },

  // Europe — remittance destinations
  { id: "london", lat: 51.5074, lng: -0.1278, city: "London", country: "GB", tier: 1 },
  { id: "paris", lat: 48.8566, lng: 2.3522, city: "Paris", country: "FR", tier: 2 },
  { id: "berlin", lat: 52.52, lng: 13.405, city: "Berlin", country: "DE", tier: 2 },
  { id: "amsterdam", lat: 52.3676, lng: 4.9041, city: "Amsterdam", country: "NL", tier: 3 },
  { id: "dublin", lat: 53.3498, lng: -6.2603, city: "Dublin", country: "IE", tier: 3 },
  { id: "madrid", lat: 40.4168, lng: -3.7038, city: "Madrid", country: "ES", tier: 3 },

  // North America
  { id: "newyork", lat: 40.7128, lng: -74.006, city: "New York", country: "US", tier: 1 },
  { id: "toronto", lat: 43.6532, lng: -79.3832, city: "Toronto", country: "CA", tier: 2 },
  { id: "dallas", lat: 32.7767, lng: -96.797, city: "Dallas", country: "US", tier: 3 },
  { id: "atlanta", lat: 33.749, lng: -84.388, city: "Atlanta", country: "US", tier: 3 },

  // MEA / Asia connectors
  { id: "dubai", lat: 25.2048, lng: 55.2708, city: "Dubai", country: "AE", tier: 1 },
  { id: "mumbai", lat: 19.076, lng: 72.8777, city: "Mumbai", country: "IN", tier: 2 },
  { id: "singapore", lat: 1.3521, lng: 103.8198, city: "Singapore", country: "SG", tier: 3 },
  { id: "istanbul", lat: 41.0082, lng: 28.9784, city: "Istanbul", country: "TR", tier: 3 },
  { id: "shenzhen", lat: 22.5431, lng: 114.0579, city: "Shenzhen", country: "CN", tier: 3 },
];

export const CITY_BY_ID = new Map(CITIES.map((c) => [c.id, c]));

/** Weighted corridor list. Each entry: [origin, dest, weight, mu, sigma] */
type CorridorDef = [CityId, CityId, number, number, number];

/**
 * weight = relative pick probability
 * mu/sigma = log-normal params for USD amount generator
 * Remittance corridors skew small-frequent; business corridors skew large-rare.
 */
export const CORRIDORS: CorridorDef[] = [
  // Intra-East-Africa — high frequency, low ticket
  ["kampala", "nairobi", 90, 3.8, 0.7],
  ["nairobi", "kampala", 85, 3.7, 0.7],
  ["nairobi", "kigali", 55, 3.9, 0.8],
  ["kigali", "kampala", 40, 3.8, 0.7],
  ["nairobi", "daressalaam", 45, 3.9, 0.8],
  ["mombasa", "nairobi", 30, 3.6, 0.6],

  // Nigeria domestic + cross-border
  ["lagos", "abuja", 70, 4.0, 0.8],
  ["abuja", "lagos", 60, 4.0, 0.8],
  ["lagos", "accra", 55, 4.1, 0.9],

  // Diaspora remittance — core volume
  ["london", "lagos", 120, 4.5, 1.1],
  ["london", "nairobi", 95, 4.3, 1.0],
  ["london", "accra", 80, 4.4, 1.0],
  ["london", "kampala", 55, 4.2, 0.9],
  ["london", "johannesburg", 40, 4.6, 1.0],
  ["newyork", "lagos", 90, 4.7, 1.2],
  ["newyork", "accra", 60, 4.6, 1.1],
  ["newyork", "nairobi", 50, 4.5, 1.0],
  ["toronto", "lagos", 45, 4.6, 1.1],
  ["dallas", "lagos", 30, 4.5, 1.0],
  ["atlanta", "accra", 25, 4.5, 1.0],
  ["paris", "casablanca", 45, 4.3, 0.9],
  ["paris", "lagos", 35, 4.5, 1.0],
  ["berlin", "lagos", 30, 4.5, 1.0],
  ["amsterdam", "accra", 20, 4.4, 0.9],
  ["dublin", "lagos", 20, 4.4, 0.9],
  ["madrid", "lagos", 20, 4.4, 0.9],

  // Gulf corridors
  ["dubai", "lagos", 60, 4.8, 1.2],
  ["dubai", "nairobi", 55, 4.7, 1.1],
  ["dubai", "mumbai", 70, 4.5, 1.0],
  ["dubai", "addis", 25, 4.3, 0.9],

  // Business / B2B — rarer, larger
  ["lagos", "shenzhen", 15, 5.8, 1.3],
  ["nairobi", "shenzhen", 12, 5.7, 1.3],
  ["johannesburg", "london", 20, 5.2, 1.2],
  ["lagos", "dubai", 25, 5.0, 1.2],
  ["nairobi", "london", 30, 4.8, 1.1],
  ["istanbul", "addis", 12, 4.4, 1.0],
  ["cairo", "london", 18, 4.6, 1.0],
  ["singapore", "nairobi", 8, 5.5, 1.3],
];

/** Currencies by country for realistic display. */
export const CURRENCY_BY_COUNTRY: Record<string, string> = {
  NG: "NGN",
  KE: "KES",
  UG: "UGX",
  RW: "RWF",
  TZ: "TZS",
  GH: "GHS",
  ZA: "ZAR",
  EG: "EGP",
  ET: "ETB",
  MA: "MAD",
  GB: "GBP",
  FR: "EUR",
  DE: "EUR",
  NL: "EUR",
  IE: "EUR",
  ES: "EUR",
  US: "USD",
  CA: "CAD",
  AE: "AED",
  IN: "INR",
  SG: "SGD",
  TR: "TRY",
  CN: "CNY",
};
